// #region ZOID TYPES

import { getFileNameFromPath } from '@utils/file-utils';
import { CaseDetails, CaseReferenceDetails, CommonIP, CommonIPLite, IP, MatterDetails, TrademarkDetails, WsFile } from './ftf-api.model';

/**
 * Represents the various events that can be emitted by a Zoid component.
 *
 * @typedef {Object} ZoidEvents
 * @property {string} RENDER - Event emitted when the Zoid component starts rendering.
 * @property {string} RENDERED - Event emitted when the Zoid component has finished rendering.
 * @property {string} PRERENDER - Event emitted before the Zoid component starts rendering.
 * @property {string} PRERENDERED - Event emitted after the Zoid component has finished pre-rendering.
 * @property {string} DISPLAY - Event emitted when the Zoid component is displayed.
 * @property {string} ERROR - Event emitted when an error occurs in the Zoid component.
 * @property {string} CLOSE - Event emitted when the Zoid component is closed.
 * @property {string} DESTROY - Event emitted when the Zoid component is destroyed.
 * @property {string} PROPS - Event emitted when the properties of the Zoid component are updated.
 * @property {string} RESIZE - Event emitted when the Zoid component is resized.
 * @property {string} FOCUS - Event emitted when the Zoid component gains focus.
 */
export type ZoidEvents = {
  RENDER: "zoid-render",
  RENDERED: "zoid-rendered",
  PRERENDER: "zoid-prerender",
  PRERENDERED: "zoid-prerendered",
  DISPLAY: "zoid-display",
  ERROR: "zoid-error",
  CLOSE: "zoid-close",
  DESTROY: "zoid-destroy",
  PROPS: "zoid-props",
  RESIZE: "zoid-resize",
  FOCUS: "zoid-focus",
};
export type ZoidGlobals = { EVENT: ZoidEvents };

/**
 * Interface representing the base properties for the global Zoid `xprops` object.
 *
 * @template T - The type of the properties.
 * @template EXPORTS - The type of the export callbacks.
 */
export interface ZoidXProps<T, EXPORTS> {
  /**
   * Callback function that if set will get called when the widget properties get updated.
   *
   * @param propsCallback - A function that takes a partial object of type T and updates the properties.
   */
  onProps?: (propsCallback: (props: Partial<Pick<T, keyof T>>) => void) => void;

  /**
   * Allows widget to export functions to the parent to use.
   *
   * @param callbacks - An object containing the export callbacks of type EXPORTS.
   */
  export?: (callbacks: EXPORTS) => void;
}

/**
 * Interface representing an instance of a Zoid widget.   This handle is returned when a widget is created.
 * and is used by the parent to interact with the widget.
 *
 * @template T - The type of the props that the widget accepts.
 */
export interface ZoidWidgetInstance<T> {
  /**
   * Renders the widget into the specified HTML element.
   *
   * @param element - The HTML element where the widget will be rendered.
   * @returns A promise that resolves when the widget has been rendered.
   */
  render(element: HTMLElement): Promise<void>;

  /**
   * Updates the properties of the widget.
   *
   * @param props - An object containing the properties to update.
   * @returns A promise that resolves when the properties have been updated.
   */
  updateProps(props: Partial<{ [K in keyof T]: T[K] | null }>): Promise<void>;

  /**
   * Closes the widget.
   *
   * @returns A promise that resolves when the widget has been closed.
   */
  close(): Promise<void>;

  /**
   * An event object that allows subscribing to widget events.
   */
  event: {
    /**
     * Subscribes to a specified event.
     *
     * @param event - The name of the event to subscribe to.  Please see `ZoidEvents` for a list of possible events.
     * @param callback - The callback function to execute when the event is triggered.
     */
    on: (event: string, callback: (data: unknown) => void) => void;
  };
}

/**
 * Represents a Zoid widget factory function.
 *
 * @template T - The type of the options object that the widget factory function accepts.
 * @template EXPORTS - The type of the additional properties or methods that the widget instance will have.
 *
 * @param options - The options object used to configure the widget instance.
 * @returns An instance of the widget that includes both the properties and methods of `ZoidWidgetInstance<T>` and the additional properties or methods defined by `EXPORTS`.
 */
export type ZoidWidget<T, EXPORTS> = (options: T) => ZoidWidgetInstance<T> & EXPORTS;

// #endregion

// #region FTF WIDGET TYPES

/*
 * NOTE:  These types might have minor overlap with those in ftf-api.model.ts
 *        but they are kept separate.  We want these types types to use non-FTF specific
 *        wording and be more generic.
 *
 * !!!!!IMPORTANT!!!!! - update widget.js to match these types.
 */


export type IPTrifoldTypes = 'Patent' | 'Trademark' | 'Matter';
export type IPTypes = IPTrifoldTypes | 'Reference';

export interface Country {
  name: string;
}

/**
 * Interface representing an case reference.
 */
export interface IPCaseRef {
    type: IPTrifoldTypes;
    caseRefNum: string;
}

/**
 * Represents the details of an case.
 *  (that are in common between all types of cases)
 */
export interface IPCase extends IPCaseRef {
  familyNum: string;
  appSerNum?: string;
  caseRefTitle?: string;
  country?: Country;
  filingDate?: string;
  // add more properties here as needed
}

export interface PatentIPCase extends IPCase {
}

export interface TrademarkIPCase extends IPCase {
}

export interface MatterIPCase extends IPCase {
}

/**
 * Represents a reference to a document.
 */
export interface DocumentRef {
  /**
   * The unique identifier of the document.
   */
  id: number;
  /**
   * The file name of the document.
   */
  fileName: string;
}

/**
 * Represents a sub-type of a document.  This is the child classification of a document type.
 */
export interface DocumentSubType {
  id: number;
  name: string;
  usptoDocTypeCode?: string;
}

/**
 * Represents a type of document.  This is the parent classification.
 */
export interface DocumentType {
  id: number;
  name: string;
}

export interface ReferenceType {
  id: number;
  name: 'Non-Patent Reference' | 'Foreign Patent' | 'US Patent'
}

/**
 * Represents the detail of a document.
 *
 * @extends DocumentRef
 */
export interface DocumentBase extends DocumentRef {
  /**
   * The case this document is associated with.
   */
  relatedCase?: IPCaseRef;

  /**
   * The size of the document in bytes.
   */
  size?: number;

  /**
   * Whether the document is available for download.
   */
  downloadable: boolean;
}


/**
 * Represents the detail of a trifold document.
 *
 * @extends DocumentBase
 */
export interface Document extends DocumentBase {
  /**
   * The date of the document.
   */
  docDate?: string;

  /**
   * The type of the document.
   */
  docType?: DocumentType;

  /**
   * The sub type of the document.
   */
  docSubType?: DocumentSubType;

}

/**
 * Represents a reference document that extends the properties and behavior
 * of a `DocumentRef`. This interface can be used to define additional
 * attributes or methods specific to reference documents.
 */
export interface ReferenceDocument extends DocumentBase {
  /**
   * The type of reference document
   */
  refType?: ReferenceType;
  /**
   * The name of the Reference.  This is a unique identifier for the reference.
   */
  refName: string;
}

/**
 * Represents a document and its base64 encoded contents
 */
export interface DocumentDownload extends DocumentRef {
  /**
   * The base64 encoded contents of the document.
   */
  base64: string
}

/**
 * Represents the "Send To" action performed on a document.
 */
export interface DocumentAction {
  /**
   * The case the documents are associated with.
   */
  case: IPCase;

  /**
   * The documents that are being acted upon.
   */
  selection: DocumentSelections;
}

export interface TrifoldFilters {
  docSubtypeId?: number[];
}

/**
 * Represents the selections of documents within the application.
 *
 * @property reference - An array of reference documents.
 * @property trifold - An array of trifold documents.
 */
export interface DocumentSelections {
  reference: ReferenceDocument[];
  trifold: Document[];
}


/**
 * Interface representing the exports for the FirstToFile widget.
 * Provides methods for downloading documents and opening cases by reference or serial number.
 */
export interface FirstToFileWidgetExports {
  /**
   * Downloads a document based on its ID, name, and type.
   *
   * @param id - The unique identifier of the document to download.
   * @param name - The name of the document.
   * @param type - The type of the case the document belongs to (if a trifold document), otherwise 'Reference'
   * @returns A promise that resolves to a `DocumentDownload` object containing the downloaded document details.
   */
  downloadDocument: (id: number, name: string, type: IPTypes) => Promise<DocumentDownload>;

  /**
   * Opens a case by its reference case number.
   *
   * @param caseRefNum - The case reference number string of the case to open.
   * @param type - (Optional) The type of the case, represented as an `IPTrifoldTypes` enum.
   *              If not provided, it will open all types of cases (based on FTF access).
   * @param filters - (Optional) Filters to apply when opening the case.
   */
  openByCaseRefNum: (caseRefNum: string, type?: IPTrifoldTypes, filters?: TrifoldFilters) => Promise<void>;

  /**
   * Opens a case by its serial number.
   *
   * @param appSerNum - The application serial number of the case to open.
   * @param type - (Optional) The type of the case, represented as an `IPTrifoldTypes` enum or type.
   *              If not provided, it will open all types of cases (based on FTF access).
   * @param filters - (Optional) Filters to apply when opening the case.
   */
  openByAppSerNum: (appSerNum: string, type?: IPTrifoldTypes, filters?: TrifoldFilters) => Promise<void>;
}

export const FIRST_TO_FILE_WIDGET_FEATURE_FLAG_KEYS = [
  /**
   * Enable the upload feature in the widget.
   */
  'upload',
  /**
   * Enable the "Open in IPMS" feature in the widget.
   */
  'openInIpms',
  /**
   * Enable the Ability to perform workflow actions in the widget.
   */
  'workflowActions',
  /**
   * Enable the ability to preview documents in the widget.
   */
  'documentPreview',
  /**
   * Enable the ability to download documents in the widget.
   */
  'documentDownload',
  /**
   * Enable the ability to for the widget document send to action
   */
  'documentSendToAction',
  /**
   * Enable the ability to change status of a document in the widget.
   *   (ex: Lock, Unlock, Checkout, Undo Checkout, etc.)
   */
  'documentChangeStatus',
  /**
   * Enable access to Patent cases in the widget.
   *    NOTE: if enabled, access is also gated by the user's FTF access.
   */
  'patent',
  /**
   * Enable access to Trademark cases in the widget.
   *    NOTE: if enabled, access is also gated by the user's FTF access.
   */
  'trademark',
  /**
   * Enable access to Matter cases in the widget.
   *    NOTE: if enabled, access is also gated by the user's FTF access.
   */
  'matter',
] as const;

export type FirstToFileWidgetFeatureFlagKey = typeof FIRST_TO_FILE_WIDGET_FEATURE_FLAG_KEYS[number];

/**
 * Feature flags to enable/disable certain actions in the widget.
 *    If not set, the widget will default to enabling features.
 */
export type FirstToFileWidgetFeatureFlags = {
  [K in FirstToFileWidgetFeatureFlagKey]?: boolean;
}
/**
 * The feature flags in addin are not optional, so this type represents all the widget flags + the defaults filled out.
 */
export type FirstToFileFeatureFlags = {
  [K in FirstToFileWidgetFeatureFlagKey]: boolean;
}

export type OrientationType = 'Auto' | 'Vertical' | 'Horizontal';

export interface FirstToFileWidgetXProps extends ZoidXProps<FirstToFileWidgetXProps, FirstToFileWidgetExports> {
  /*
   * Add any parameters that you want to pass to the widget here
   *
   * NOTE!!!: make sure these line up with assets/widget/widget.js
   */

  /**
   * The name of the product implementing widget.  This will appear for certain actions inside FTF widget.
   *   e.x. "Open in {productName}"
   */
  productName?: string;
  /**
   * The orientation of the widget.
   */
  orientation?: OrientationType;

  /**
   * Feature flags to enable/disable certain actions in the widget.  If not set, the widget will default to enabling features.
   */
  featureFlags?: FirstToFileWidgetFeatureFlags;

  /**
   * Callback that widget will call when it is loaded.
   * @returns
   */
  onLoad?: () => void;
  /**
   * Callback that widget will call when it is authenticated.
   * @returns
   */
  onAuth?: () => void;
  /**
   * Callback that widget will call when a new case trifold is opened.
   * @param item the case that was opened.
   * @returns
   */
  onCaseOpen?: (item: IPCase) => void;
  /**
   * Callback that widget will call when user performs a "Send To" action document(s).
   * @param action Object with the case and documents that were acted upon.
   * @returns
   */
  onDocumentAction?: (action: DocumentAction) => void;
  /**
   * Callback that wiget will call when it attempts to execute a command.
   *   See FirstToFileWidgetExports for available commands.
   * @param command The command that was ran.  See FirstToFileWidgetExports for available commands.
   * @param args The arguments that were supplied to the command.
   * @returns
   */
  onCommand?: (command: keyof FirstToFileWidgetExports, args: []) => void;
}

/**
 * Represents the keys of the event callbacks that can be passed to the FirstToFile widget.
 */
export type FirstToFileWidgetEventKeys = Exclude<{
  [K in keyof FirstToFileWidgetXProps]: K extends `on${string}` ? K : never;
}[keyof FirstToFileWidgetXProps], undefined>;

/**
 * Type representing the factory method that can instantiate a FirstToFile widget and return an instance.
 */
export type FirstToFileWidgetType = ZoidWidget<FirstToFileWidgetXProps, FirstToFileWidgetExports>;
/**
 * Type represents an instance of a FirstToFile widget.
 */
export type FirstToFileWidgetInstance = ZoidWidgetInstance<FirstToFileWidgetXProps> & FirstToFileWidgetExports;

/*
  Helper methods for conversion between FTF and Widget Models
*/

export function convertCommonIPLiteToIPCaseRef(from: CommonIPLite): IPCaseRef {
  return {
    type: from.ipType,
    caseRefNum: from.identifier,
  };
}

export function convertCommonIPToIPCase(from: CommonIP): PatentIPCase | TrademarkIPCase | MatterIPCase {
  const biblio = from.detail!.biblio;
  // fields specific to Patent, Trademark, or Matter cases
  if (from.ipType === IP.TrifTypeEnum.Trademark) {
    return convertCommonIPAndDetailsToTrademarkIPCase(from, biblio as TrademarkDetails);
  } else if (from.ipType === IP.TrifTypeEnum.Matter) {
    return convertCommonIPAndDetailsToMatterIPCase(from, biblio as MatterDetails);
  } else {
    return convertCommonIPAndDetailsToPatentIPCase(from, biblio as CaseDetails);
  }
}

export function convertCommonIPAndDetailsToPatentIPCase(from: CommonIP, biblio: CaseDetails): PatentIPCase {
  const baseCaseRef = convertCommonIPLiteToIPCaseRef(from);

  const common: IPCase = {
    ...baseCaseRef,
    caseRefTitle: from.title,
    appSerNum: from.serial,
    // currently we don't have country code in api, so just set name of country
    country: biblio.country ? { name: biblio.country } : undefined,
    familyNum: biblio.caseFamilyNm!,
    filingDate: biblio.fileDate,
  };

  return {
    ...common,
    // TODO: more needs added here eventually based on iptype.
  };
}

export function convertCommonIPAndDetailsToTrademarkIPCase(from: CommonIP, biblio: TrademarkDetails): TrademarkIPCase {
  const baseCaseRef = convertCommonIPLiteToIPCaseRef(from);

  const common: IPCase = {
    ...baseCaseRef,
    caseRefTitle: from.title,
    appSerNum: from.serial,
    // currently we don't have country code in api, so just set name of country
    country: biblio.country ? { name: biblio.country } : undefined,
    familyNum: biblio.caseFamily!,
    filingDate: biblio.filingDate,
  };

  return {
    ...common,
    // TODO: more needs added here eventually based on iptype.
  };
}

export function convertCommonIPAndDetailsToMatterIPCase(from: CommonIP, biblio: MatterDetails): MatterIPCase {
  const baseCaseRef = convertCommonIPLiteToIPCaseRef(from);

  const common: IPCase = {
    ...baseCaseRef,
    caseRefTitle: from.title,
    appSerNum: from.serial,
    // currently we don't have country code in api, so just set name of country
    country: biblio.country ? { name: biblio.country } : undefined,
    familyNum: biblio.matterFamilyNm!,
    filingDate: biblio.filingDate,
  };

  return {
    ...common,
    // TODO: more needs added here eventually based on iptype.
  };
}

export function convertWsFileToDocumentRef(from: WsFile): DocumentRef {
  return {
    id: from.fileId!,
    fileName: from.fileName!,
  };
}

export function convertWsFileToDocument(from: WsFile, item?: CommonIPLite): Document {
  const baseDocumentRef = convertWsFileToDocumentRef(from);

  return {
    ...baseDocumentRef,
    docDate: from.documentDate,
    docType: from.docTypeId && from.docType ? { id: from.docTypeId, name: from.docType } : undefined,
    docSubType: from.docSubtypeId && from.docSubtype ? { id: from.docSubtypeId!, name: from.docSubtype! } : undefined,
    downloadable: !/\.placeHolder$/i.test(from.fileName!),
    size: from.fileSize,
    relatedCase: item ? convertCommonIPLiteToIPCaseRef(item) : undefined,
  };
}

export function convertCaseRefToReferenceDocument(from: CaseReferenceDetails, item?: CommonIPLite): ReferenceDocument {
  return {
    id: from.fileId!,
    fileName: getFileNameFromPath(from.fileName!),
    refName: from.titleDisplay!,
    refType: from.referenceType && from.referenceTypeDisplay ? { id: from.referenceType!, name: from.referenceTypeDisplay! } as ReferenceType : undefined,
    downloadable: !/\.placeHolder$/i.test(from.fileName!),
    size: from.fileSize,
    relatedCase: item ? convertCommonIPLiteToIPCaseRef(item) : undefined,
  };
}

// #endregion

declare global {
  interface Window {
    /**
     * The global Zoid object that allows the widget to interact with the parent
     */
    xprops: FirstToFileWidgetXProps;

    /**
     * The global object that contains the Zoid event constants.
     */
    zoid: ZoidGlobals;
  }
}
